import { DataSource, FiltrosModel } from '@/datasources/consulte-grid';
import { FiltroEstudanteModel } from '@/models/filtros/filtro-estudante.model';
import api from '@/services/axios/api';
import { useStore } from '@/store';
import { AxiosResponse } from 'axios';

/** Recorte de situação na escola das declarações da secretaria (ver SituacaoNaEscolaSql na API). */
export type RecorteSituacaoNaEscola = 'Transferidos' | 'Efetivos' | 'Aprovados';

/**
 * Recorte opcional da pesquisa. Sem ele o endpoint devolve todo aluno cadastrado na escola —
 * inclusive quem nunca foi enturmado, que não serve de origem para rematrícula nem para as telas
 * que trabalham sobre a turma.
 */
export interface FiltroEstudantePesquisa {
  contextoAtendimento?: boolean;
  /** Ano da enturmação e da matrícula exigidas. */
  ano?: number | null;
  /** Só quem está enturmado E matriculado no ano. */
  somenteEnturmadosMatriculados?: boolean;
  /** Turma da enturmação. Zero aceita qualquer turma da escola. */
  codigoTurma?: number | null;
  tipoEnturmacao?: number | null;
  /**
   * Só quem TEM matrícula na escola (no `ano`, quando informado), independentemente de estar ativo
   * ou enturmado — inclui o aluno transferido. É o recorte da reemissão de boletim/ficha: casa com
   * o filtro que lista as matrículas do aluno depois da seleção, para não oferecer quem cairia em
   * "sem matrícula".
   */
  somenteMatriculadosNaEscola?: boolean;
  /**
   * Recorte de SITUAÇÃO na escola, no `ano`, das declarações da secretaria: `Transferidos`
   * (Transferência), `Efetivos` (Escolaridade) ou `Aprovados` (Conclusão). Vai pelo NOME do
   * valor, que é como a API lê o enum. Vazio = sem recorte (todas as outras telas).
   */
  recorteSituacaoNaEscola?: RecorteSituacaoNaEscola | '';
  /**
   * Ignora a escola e pesquisa na REDE inteira. Combina com `status`: é o que separa
   * "matriculados nesta escola" de "matriculados na rede".
   */
  desconsiderarEscola?: boolean;
  /**
   * Recorte de situação no ano: `Desconsiderar`, `Enturmados`, `Matriculados`, `NaoEnturmados`,
   * `NaoMatriculados`. Viaja como NOME — a API serializa enums com `JsonStringEnumConverter`.
   */
  status?: string | null;
}

class FiltroEstudanteService {
  private codigoEscola: string | null = null;
  private ano: string | null = null;
  private semestre: string | null = null;

  constructor() {
    this.initialize();
  }

  private initialize(): void {
    const store = useStore();
    this.codigoEscola = store.getters.getCodigoEscola;
    this.ano = store.getters.getAno;
    this.semestre = store.getters.getSemestre;
  }

  /**
    Método para obter dados paginados de estudantes
   * @param pagina Número da página atual
   * @param itensPorPagina Número de itens por página
   * @param filters Lista de filtros aplicados
   * @param pessoalId ID do usuário pessoal, se aplicável
   * @returns Promise com os dados paginados de estudantes
   */
  async dadosIndexPaginado(
    pagina: number,
    itensPorPagina: number,
    filters: FiltrosModel[],
    // novo parâmetro opcional: quando informado e > 0, será usado na requisição
    escolaId?: number | null,
    // mantém compatibilidade com uso anterior (pessoalId permanece opcional)
    pessoalId: number | null = null,
    pesquisa?: FiltroEstudantePesquisa,
  ): Promise<AxiosResponse<DataSource<FiltroEstudanteModel>>> {
    this.initialize(); // Atualiza variáveis de contexto

    const payload = {
      rowsPerPage: itensPorPagina,
      pageNumber: pagina,
      filters: filters,
    };

    // Se `escolaId` for informado e maior que zero, utiliza-o; caso contrário,
    // mantém a lógica anterior usando `this.codigoEscola`.
    const escolaParam =
      escolaId !== undefined && escolaId !== null && Number(escolaId) > 0
        ? String(escolaId)
        : this.codigoEscola;

    const url = pesquisa?.contextoAtendimento
      ? `/AtendimentoAnalises/Estudantes/RetornoPaginado/${escolaParam}`
      : `/Estudante/RetornoPaginado/${escolaParam}`;

    // O recorte vai por query string porque a rota já carrega a escola no caminho.
    //
    // O ANO vai SEMPRE, mesmo sem recorte: é dele (com a escola da rota) que o servidor tira a
    // série e a turma mostradas — as do estudante NESTA escola NESTE ano, pela matrícula e pela
    // enturmação. Sozinho ele não filtra nada; quem filtra são as flags abaixo.
    const params: Record<string, unknown> = { anoReferencia: pesquisa?.ano ?? this.ano };

    if (pesquisa?.somenteEnturmadosMatriculados) {
      params.somenteEnturmadosMatriculados = true;

      // `anoReferencia`, e nao `ano`: o parametro `ano` da action existe num template de rota, e
      // o ASP.NET o le da rota, nunca da query string.
      params.anoReferencia = pesquisa.ano ?? this.ano;

      if (pesquisa.codigoTurma) params.codigoTurma = pesquisa.codigoTurma;
      if (pesquisa.tipoEnturmacao !== null && pesquisa.tipoEnturmacao !== undefined) {
        params.tipoEnturmacao = pesquisa.tipoEnturmacao;
      }
    }

    if (pesquisa?.somenteMatriculadosNaEscola) {
      params.somenteMatriculadosNaEscola = true;
      // Mesmo motivo do bloco acima: o ano vai como `anoReferencia`, nunca como `ano` (rota).
      params.anoReferencia = pesquisa.ano ?? this.ano;
    }

    if (pesquisa?.recorteSituacaoNaEscola) {
      params.recorteSituacaoNaEscola = pesquisa.recorteSituacaoNaEscola;
      params.anoReferencia = pesquisa.ano ?? this.ano;
    }

    /*
     * ESCOLA e SITUAÇÃO — os dois filtros que toda pesquisa de estudante oferece.
     *
     * O ano vai SEMPRE que há recorte de situação: matrícula e enturmação existem por ano, e sem
     * ele o servidor ignora o filtro (responder "não matriculado" sem dizer em que ano não
     * significa nada).
     *
     * `anoReferencia`, e não `ano`: `ano` aparece num template de rota da action e o ASP.NET o lê
     * da rota, nunca da query string.
     */
    if (pesquisa?.desconsiderarEscola) params.desconsiderarEscola = true;

    if (pesquisa?.status && pesquisa.status !== 'Desconsiderar') {
      params.status = pesquisa.status;
      params.anoReferencia = pesquisa.ano ?? this.ano;
    }

    try {
      return await api.post<DataSource<FiltroEstudanteModel>>(url, payload, { params });
    } catch (error) {
      console.error('Erro ao buscar dados paginados:', error);
      throw error;
    }
  }
}

export default new FiltroEstudanteService();
